import Select, { components } from "react-select";
import { useTheme } from "@hooks/useTheme";
import { useState, useRef, useEffect } from "react";
import { useOnClickOutside } from "@hooks/useOnClickOutside";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";

const CustomSelect = ({
  options,
  value,
  onChange,
  placeholder,
  className,
  menuPlacement = "auto",
  size = "md",
  isSearchable = true,
  maxMenuHeight = 18, // 200px = 12.5rem
  clearable = true,
  customStyles = {},
  usePortal = false, // New prop to enable portal rendering
  disabled = false, // Add disabled prop
  isMulti = false, // new prop to enable multiple choice
  hideSelectedOptions = false, // new prop to enable hiding or showing selected options
  closeMenuOnSelect = true,
  selectAllLabelKey = "select-all",
  displayFormatLabelWithID = false,
  openMenuOnClick = true, // new prop to control opening menu on click for multi-select
  "data-testid": dataTestId,
  ...props
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const wrapperRef = useRef();
  const tooltipPrefixRef = useRef(`custom-select-${Math.random().toString(36).slice(2, 9)}`);
  // Only use useOnClickOutside for multi-select functionality
  useOnClickOutside(wrapperRef, () => setMenuIsOpen(false), menuIsOpen && isMulti);

  // Helper component to render text with tooltip only when truncated
  const TruncatedTextWithTooltip = ({ text, tooltipId, className = "", style = {} }) => {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const checkTruncation = () => {
        if (textRef.current) {
          requestAnimationFrame(() => {
            if (textRef.current) {
              const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
              setIsTruncated(isOverflowing);
            }
          });
        }
      };

      const timeoutId = setTimeout(checkTruncation, 0);
      window.addEventListener('resize', checkTruncation);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', checkTruncation);
      };
    }, [text]);

    if (!text || text === "-") {
      return <span ref={textRef} className={className} style={style}>{text || "-"}</span>;
    }

    return (
      <>
        <span
          ref={textRef}
          className={className}
          style={style}
          {...(isTruncated ? {
            "data-tooltip-id": tooltipId,
            "data-tooltip-content": text,
          } : {})}
        >
          {text}
        </span>
        {isTruncated && (
          <Tooltip
            id={tooltipId}
            place="right"
            offset={12}
            positionStrategy="fixed"
            className="!text-xs !rounded-lg !px-3 !py-2 !max-w-[500px] !z-50"
            style={{
              backgroundColor: colors.headerTooltipBackgroundColor,
              color: colors.headerTooltipTextColor,
              fontSize: '12px',
              borderRadius: '8px',
              padding: '8px 12px',
              maxWidth: '500px',
              width: 'max-content',
              zIndex: 1000,
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
              wordBreak: 'break-word'
            }}
          />
        )}
      </>
    );
  };

  const customDisplayLabel = (props) => {
    const { data, isSelected, isFocused } = props;
    const isActive = isSelected || isFocused;
    const textColor = isActive ? colors.textActive : colors.textPrimary;
    const subTextColor = isActive ? colors.textActive : colors.textSubTitle;
    const mainValue = data["label"] || "-";
    const subValue = data["value"] || "-";
    const baseId = `${tooltipPrefixRef.current}-label-${data.value ?? data.label}`;

    return (
      <div className={`flex gap-1 ${textColor}`}>
        <TruncatedTextWithTooltip
          text={mainValue}
          tooltipId={`${baseId}-main`}
          className={`truncate ${textColor}`}
        />
        <TruncatedTextWithTooltip
          text={`(${subValue})`}
          tooltipId={`${baseId}-sub`}
          className={`italic truncate ${subTextColor}`}
        />
      </div>
    );
  };

  // If multi select is enabled → add "Select All" option at the top
  const enhancedOptions = isMulti
    ? [{ value: "*", label: t(selectAllLabelKey) }, ...options]
    : options;

  const customValueContainer = ({ children, ...props }) => {
    const { selectProps } = props;
    const { options } = selectProps;
    if (props.isMulti) {
      const values = props.getValue();
      const allSelected = Array.isArray(values) && values.length === options.length - 1;
      const labels = allSelected ? t(selectAllLabelKey) : values.map((v) => v.label).join(", ");
      
      if (values.length >= 1) {
        // Case: too long → collapse into a single truncated text
        const tooltipId = `${tooltipPrefixRef.current}-multi-value-summary`;
        return (
          <components.ValueContainer {...props}>
            <TruncatedTextWithTooltip
              text={labels}
              tooltipId={tooltipId}
              className="block"
              style={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                color: colors.textPrimaryColor
              }}
            />
          </components.ValueContainer>
        );
      }
    }

    // Default: render normally
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  };

  const checkBoxOptionLayout = (props) => {
    const { isSelected, label, data, isDisabled, selectProps  } = props;
    const { value, options } = selectProps;
    const allSelected = Array.isArray(value) && value.length === options.length - 1;

    // Determine checkbox checked state
    const checked =
      data.value === "*" ? allSelected : isSelected || allSelected;

    return (
        <components.Option {...props}>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              className="sr-only"
              checked={checked}
              readOnly
            />

            <div
              className={`relative h-4 w-4 rounded border transition-all duration-200 flex-shrink-0 
                ${isDisabled
                  ? `cursor-not-allowed ${colors.borderDisabledOutLineButton} ${colors.bgSecondary} opacity-50`
                  : checked
                    ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
                    : `${colors.borderPrimary} ${colors.borderHoverPrimary}`}
              `}
            >
              {checked && (
                <div className="absolute inset-0 flex items-center justify-center flex-shrink-0">
                  <svg
                    className={`h-3 w-3 ${colors.textNormal}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>

            <TruncatedTextWithTooltip
              text={label}
              tooltipId={`${tooltipPrefixRef.current}-checkbox-${data.value}`}
              className="overflow-hidden whitespace-nowrap text-ellipsis"
            />
          </div>
        </components.Option>
      )
  }

  const customSingleValue = (props) => {
    const { children } = props;
  
    if (displayFormatLabelWithID && props.data) {
      const mainValue = props.data.label || "-";
      const subValue = props.data.value || "-";
  
      return (
        <components.SingleValue {...props}>
          <div className={`flex gap-1 ${colors.textPrimary}`}>
            <span className={`truncate ${colors.textPrimary}`}>{mainValue}</span>
            <span className={`italic truncate ${colors.textSubTitle}`}>({subValue})</span>
          </div>
        </components.SingleValue>
      );
    }
    return <components.SingleValue {...props}>{children}</components.SingleValue>;
  }

  const defaultOptionWithTooltip = (props) => {
    const tooltipId = `${tooltipPrefixRef.current}-option-${props.data.value ?? props.label}`;
    return (
      <components.Option {...props}>
        <TruncatedTextWithTooltip
          text={props.label}
          tooltipId={tooltipId}
          className="block overflow-hidden whitespace-nowrap text-ellipsis"
        />
      </components.Option>
    );
  };

  const customOptions = ({ children, ...props}) => {
    if (props.isMulti) {
      return checkBoxOptionLayout(props);
    }

    if (displayFormatLabelWithID) {
      return (
        <components.Option {...props}>
          {customDisplayLabel(props)}
        </components.Option>
      );
    }

    return defaultOptionWithTooltip(props);
  };

  // Size configurations
  const sizeConfigs = {
    xs: {
      minHeight: "1.5rem",
      fontSize: "0.75rem",
      padding: "0px 0.5rem",
      borderRadius: "0.25rem",
    },
    sm: {
      minHeight: "2rem",
      fontSize: "0.875rem",
      padding: "0px 0.625rem",
      borderRadius: "0.375rem",
    },
    md: {
      minHeight: "2.5rem",
      fontSize: "0.875rem",
      padding: "0px 0.75rem",
      borderRadius: "0.5rem",
    },
    lg: {
      minHeight: "3rem",
      fontSize: "1.125rem",
      padding: "0px 1rem",
      borderRadius: "0.5rem",
    },
    xl: {
      minHeight: "3.5rem",
      fontSize: "1.25rem",
      padding: "0px 1.25rem",
      borderRadius: "0.75rem",
    },
  };

  const currentSize = sizeConfigs[size] || sizeConfigs.md;

  const mergedStyles = {
    control: (base, state) => {
      const baseControl = {
        ...base,
        backgroundColor: colors.bgInputColor,
        borderColor: colors.borderPrimaryColor,
        color: colors.textPlaceholderColor,
        minHeight: currentSize.minHeight,
        fontSize: currentSize.fontSize,
        borderRadius: currentSize.borderRadius,
        boxShadow: "none",
        padding: currentSize.padding,
        opacity: state.isDisabled ? 0.5 : 1,
        cursor: state.isDisabled ? "not-allowed" : "default",
        "&:hover": {
          borderColor: state.isDisabled ? colors.borderPrimaryColor : colors.borderActiveColor
        },
      };

      // If customStyles.control exists, use it and merge with base
      if (customStyles.control) {
        return customStyles.control(baseControl, state);
      }

      return baseControl;
    },
    placeholder: (base) => {
      const basePlaceholder = {
        ...base,
        color: colors.textPlaceholderColor,
        margin: 0,
        padding: 0,
        fontSize: currentSize.fontSize,
      };

      if (customStyles.placeholder) {
        return customStyles.placeholder(basePlaceholder);
      }

      return basePlaceholder;
    },
    singleValue: (base) => {
      const baseSingleValue = {
        ...base,
        margin: 0,
        padding: 0,
        color: colors.textPrimaryColor,
        fontSize: currentSize.fontSize,
      };

      if (customStyles.singleValue) {
        return customStyles.singleValue(baseSingleValue);
      }

      return baseSingleValue;
    },

    multiValue: (base) => {
      const baseMultiValue = {
        ...base,
        backgroundColor: colors.chipBg,
        borderRadius: "9999px",
        padding: "0 4px",
        display: "inline-flex",
        alignItems: "center",
        height: "24px",
      }

      if (customStyles.multiValue) {
        return customStyles.multiValue(baseMultiValue);
      }

      return baseMultiValue;
    },

    multiValueLabel: (base) => {
      const baseMultiValueLabel = {
        ...base,
        color: colors.textPrimaryColor,
        fontSize: currentSize.fontSize,          
        fontWeight: 500,            // font-medium
        padding: "0 6px",           // px-1.5
      };

      if (customStyles.multiValueLabel) {
        return customStyles.multiValueLabel(baseMultiValueLabel);
      }

      return baseMultiValueLabel;
    },

    multiValueRemove: (base) => {
      const baseMultiValueRemove = {
        ...base,
        color: colors.textPrimaryColor,
        cursor: "pointer",
        padding: 0,
        borderRadius: "50%",
        ":hover": {
          backgroundColor: colors.chipRemoveHoverBg,
          color: colors.chipRemoveHoverText,
        },
      };

      if (customStyles.multiValueRemove) {
        return customStyles.multiValueRemove(baseMultiValueRemove);
      }

      return baseMultiValueRemove;
    },


    valueContainer: (base) => {
      const baseValueContainer = {
        ...base,
        padding: 0,
      };

      if (customStyles.valueContainer) {
        return customStyles.valueContainer(baseValueContainer);
      }

      return baseValueContainer;
    },
    indicatorsContainer: (base) => {
      const baseIndicatorsContainer = {
        ...base,
        padding: 0,
        margin: 0,
      };

      if (customStyles.indicatorsContainer) {
        return customStyles.indicatorsContainer(baseIndicatorsContainer);
      }

      return baseIndicatorsContainer;
    },
    menu: (base) => {
      const baseMenu = {
        ...base,
        backgroundColor: colors.bgPrimaryColor,
        borderRadius: "0.25rem",
        marginTop: 4,
        borderTop: "none",
        boxShadow: "0px 0px 6px 1px rgba(0, 0, 0, 0.6)",
        padding: "0.125rem",
        zIndex: 9999,
      };

      if (customStyles.menu) {
        return customStyles.menu(baseMenu);
      }

      return baseMenu;
    },
    menuPortal: (base) => {
      const baseMenuPortal = {
        ...base,
        zIndex: 9999,
      };

      if (customStyles.menuPortal) {
        return customStyles.menuPortal(baseMenuPortal);
      }

      return baseMenuPortal;
    },
    menuList: (base) => {
      const baseMenuList = {
        ...base,
        borderRadius: "0.5rem",
        padding: "0.5rem",
        margin: 0,
        backgroundColor: colors.bgPrimaryColor,
        color: colors.textPrimaryColor,
        maxHeight: `${maxMenuHeight}rem`,
      };

      if (customStyles.menuList) {
        return customStyles.menuList(baseMenuList);
      }

      return baseMenuList;
    },
    option: (base, state) => {
      const isDisabled = state.data.isDisabled;

      const baseOption = {
        ...base,
        border: state.isSelected ? `1px solid ${colors.dropdownBorderColor}` : "none",
        boxShadow: state.isSelected ? "0px -0.1px 4px 1px rgba(0, 0, 0, 0.6)" : "none",
        zIndex: state.isSelected ? 1000 : 0,
        backgroundColor: colors.bgPrimaryColor,
        color: isDisabled
          ? colors.textSubTitle
          : state.isSelected
            ? colors.textActiveColor
            : colors.textPrimaryColor,
        borderRadius: "0.25rem",
        position: "relative",
        // overflow: "visible",
        fontSize: currentSize.fontSize,
        padding:
          size === "xs"
            ? "0.25rem 0.5rem"
            : size === "sm"
              ? "0.375rem 0.625rem"
              : size === "lg"
                ? "0.625rem 1rem"
                : size === "xl"
                  ? "0.75rem 1.25rem"
                  : "0.5rem 0.75rem", // md default
        cursor: isDisabled ? "not-allowed" : (isMulti ? "pointer" : "default"),
        opacity: isDisabled ? 0.5 : 1,

        "&:hover": {
          border: `1px solid ${colors.dropdownBorderColor}`,
          boxShadow: "0px -0.1px 4px 1px rgba(0, 0, 0, 0.6)",
          color: colors.textActiveColor,
        },
        "&:active": {
          backgroundColor: colors.bgPrimaryColor,
        },
        whiteSpace: "nowrap",     
        overflow: "hidden",        
        textOverflow: "ellipsis",  

        marginBottom: isMulti ? 3 : 0, //
      };

      if (customStyles.option) {
        return customStyles.option(baseOption, state);
      }

      return baseOption;
    },
    indicatorSeparator: () => null,
    dropdownIndicator: () => null,
    clearIndicator: (base) => ({
      ...base,
      color: colors.textSubTitle,
      cursor: "pointer",
      "&:hover": {
        color: colors.textActiveColor,
      },
    }),
    input: (base) => {
      const baseInput = {
        ...base,
        color: colors.textPrimaryColor,
        fontSize: currentSize.fontSize,
        margin: 0,
        padding: 0,
      };

      if (customStyles.input) {
        return customStyles.input(baseInput);
      }

      return baseInput;
    },
  };

  // Custom Control component to handle click behavior for multi-select
  const CustomControl = (props) => {
    const { selectProps, innerRef, innerProps } = props;
    
    const handleMouseDown = (event) => {
      // If multi-select and openMenuOnClick = true, toggle menu when clicking on control area
      if (isMulti && openMenuOnClick && !disabled) {
        event.preventDefault();
        setMenuIsOpen(prev => !prev);
        return; // Prevent default behavior
      }
      
      // Call original onMouseDown if it exists
      if (innerProps.onMouseDown) {
        innerProps.onMouseDown(event);
      }
    };

    return (
      <components.Control
        {...props}
        innerProps={{
          ...innerProps,
          onMouseDown: handleMouseDown,
        }}
      />
    );
  };

  const handleChange = (selectedOption) => {
    if (!isMulti) {
      onChange(selectedOption);
      return;
    }

    // If cleared → return null
    if (!selectedOption) {
      onChange(null);
      return;
    }

    // Check if user selected the "Select All" option
    if (selectedOption.some((s) => s.value === "*")) {
        if (value && value.length === options.length) {
          // Already all selected → unselect all
          onChange([]);
        } else {
          // Select all real options
          onChange(options);
        }
    } else {
      // Normal case: just forward the selected values
      onChange(selectedOption);
    }
  };

  return (
    <div ref={wrapperRef} data-testid={dataTestId}>
      <Select
        isMulti={isMulti}
        options={enhancedOptions}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        className={className}
        styles={mergedStyles}
        menuPlacement={menuPlacement}
        isSearchable={isSearchable}
        maxMenuHeight={maxMenuHeight}
        isClearable={clearable}
        isDisabled={disabled} // Add this line
        menuPortalTarget={usePortal ? document.body : undefined}
        menuPosition={usePortal ? "fixed" : "absolute"}
        hideSelectedOptions={hideSelectedOptions}
        components={{ 
          ValueContainer: customValueContainer, 
          Option: customOptions, 
          SingleValue: customSingleValue,
          Control: isMulti && openMenuOnClick ? CustomControl : components.Control
        }}
        closeMenuOnSelect={closeMenuOnSelect}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        menuIsOpen={isMulti ? menuIsOpen : undefined}
        {...props}
      />
    </div>
  );
};

export default CustomSelect;
